// services/distanceService.js

// Free geocoding API (you can replace with Google Maps API key)
const NOMINATIM_API = 'https://nominatim.openstreetmap.org/search';
const OSRM_API = 'https://router.project-osrm.org/route/v1/driving';

// Cache for storing geocoded locations and distances
const locationCache = new Map();
const distanceCache = new Map();
const searchCache = new Map();

const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const ERROR_CACHE_TTL_MS = 15 * 1000;
const getCachedSearchResults = (query) => {
  const cached = searchCache.get(query);
  if (!cached) return null;
  const ttlMs = cached.ttlMs ?? SEARCH_CACHE_TTL_MS;
  if (Date.now() - cached.ts > ttlMs) {
    searchCache.delete(query);
    return null;
  }
  return cached.results;
};
const cacheSearchResults = (query, results, ttlMs = SEARCH_CACHE_TTL_MS) => {
  searchCache.set(query, { ts: Date.now(), ttlMs, results });
};

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, { signal: options.signal });
  if (!response.ok) {
    const err = new Error(`Request failed: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

const normalizeNominatimResults = (data, query) => {
  return (Array.isArray(data) ? data : [])
    .map((item) => {
      const address = item.address || {};
      const city =
        address.city ||
        address.town ||
        address.village ||
        address.county ||
        address.state_district ||
        '';
      const state = address.state || '';
      const displayName = item.display_name || '';
      const mainText = city || (displayName ? displayName.split(',')[0] : query);
      const secondaryText =
        state ||
        address.country ||
        (displayName ? displayName.split(',').slice(1, 3).join(',').trim() : '');

      return {
        city: mainText,
        state: secondaryText,
        displayName,
        description: displayName,
        address: displayName,
        lat: item.lat ? parseFloat(item.lat) : undefined,
        lng: item.lon ? parseFloat(item.lon) : undefined,
        structured_formatting: {
          main_text: mainText,
          secondary_text: secondaryText
        }
      };
    })
    .filter((r) => r.displayName);
};

const normalizeOpenMeteoResults = (data, query) => {
  const results = data?.results;
  return (Array.isArray(results) ? results : [])
    .map((item) => {
      const name = item.name || query;
      const admin1 = item.admin1 || '';
      const admin2 = item.admin2 || '';
      const country = item.country || '';
      const secondaryText = [admin2, admin1, country].filter(Boolean).join(', ');
      const displayName = [name, secondaryText].filter(Boolean).join(', ');

      return {
        city: name,
        state: secondaryText,
        displayName,
        description: displayName,
        address: displayName,
        lat: typeof item.latitude === 'number' ? item.latitude : undefined,
        lng: typeof item.longitude === 'number' ? item.longitude : undefined,
        structured_formatting: {
          main_text: name,
          secondary_text: secondaryText
        }
      };
    })
    .filter((r) => r.displayName);
};

const normalizePhotonResults = (data, query) => {
  const features = data?.features;
  return (Array.isArray(features) ? features : [])
    .map((feature) => {
      const props = feature?.properties || {};
      const coords = feature?.geometry?.coordinates;
      const lon = Array.isArray(coords) ? coords[0] : undefined;
      const lat = Array.isArray(coords) ? coords[1] : undefined;

      const name = props.name || props.street || props.city || props.state || query;
      const city = props.city || props.county || '';
      const state = props.state || '';
      const country = props.country || '';
      const postcode = props.postcode || '';

      const secondaryText = [city, state, postcode || null, country].filter(Boolean).join(', ');
      const displayName = [name, secondaryText].filter(Boolean).join(', ');

      return {
        city: name,
        state: secondaryText,
        displayName,
        description: displayName,
        address: displayName,
        lat: typeof lat === 'number' ? lat : undefined,
        lng: typeof lon === 'number' ? lon : undefined,
        structured_formatting: {
          main_text: name,
          secondary_text: secondaryText
        }
      };
    })
    .filter((r) => r.displayName);
};

// Enhanced location database with more cities and towns
const LOCATION_DATABASE = [
  // Major Cities
  { city: 'Mumbai', state: 'Maharashtra', displayName: 'Mumbai, Maharashtra', lat: 19.0760, lng: 72.8777 },
  { city: 'Delhi', state: 'Delhi', displayName: 'Delhi, NCR', lat: 28.7041, lng: 77.1025 },
  { city: 'Bangalore', state: 'Karnataka', displayName: 'Bangalore, Karnataka', lat: 12.9716, lng: 77.5946 },
  { city: 'Chennai', state: 'Tamil Nadu', displayName: 'Chennai, Tamil Nadu', lat: 13.0827, lng: 80.2707 },
  { city: 'Kolkata', state: 'West Bengal', displayName: 'Kolkata, West Bengal', lat: 22.5726, lng: 88.3639 },
  { city: 'Hyderabad', state: 'Telangana', displayName: 'Hyderabad, Telangana', lat: 17.3850, lng: 78.4867 },
  { city: 'Pune', state: 'Maharashtra', displayName: 'Pune, Maharashtra', lat: 18.5204, lng: 73.8567 },
  { city: 'Ahmedabad', state: 'Gujarat', displayName: 'Ahmedabad, Gujarat', lat: 23.0225, lng: 72.5714 },
  { city: 'Jaipur', state: 'Rajasthan', displayName: 'Jaipur, Rajasthan', lat: 26.9124, lng: 75.7873 },
  { city: 'Lucknow', state: 'Uttar Pradesh', displayName: 'Lucknow, Uttar Pradesh', lat: 26.8467, lng: 80.9462 },
  
  // Tamil Nadu Cities & Towns
  { city: 'Kundrathur', state: 'Tamil Nadu', displayName: 'Kundrathur, Chennai, Tamil Nadu', lat: 12.9958, lng: 80.0985 },
  { city: 'Tirupati', state: 'Andhra Pradesh', displayName: 'Tirupati, Andhra Pradesh', lat: 13.6288, lng: 79.4192 },
  { city: 'Tambaram', state: 'Tamil Nadu', displayName: 'Tambaram, Chennai, Tamil Nadu', lat: 12.9243, lng: 80.1186 },
  { city: 'Pallavaram', state: 'Tamil Nadu', displayName: 'Pallavaram, Chennai, Tamil Nadu', lat: 12.9675, lng: 80.1495 },
  { city: 'Chromepet', state: 'Tamil Nadu', displayName: 'Chromepet, Chennai, Tamil Nadu', lat: 12.9512, lng: 80.1402 },
  { city: 'Poonamallee', state: 'Tamil Nadu', displayName: 'Poonamallee, Chennai, Tamil Nadu', lat: 13.0489, lng: 80.0972 },
  { city: 'Avadi', state: 'Tamil Nadu', displayName: 'Avadi, Chennai, Tamil Nadu', lat: 13.1147, lng: 80.1018 },
  { city: 'Ambattur', state: 'Tamil Nadu', displayName: 'Ambattur, Chennai, Tamil Nadu', lat: 13.1143, lng: 80.1548 },
  { city: 'Porur', state: 'Tamil Nadu', displayName: 'Porur, Chennai, Tamil Nadu', lat: 13.0358, lng: 80.1582 },
  { city: 'Maduravoyal', state: 'Tamil Nadu', displayName: 'Maduravoyal, Chennai, Tamil Nadu', lat: 13.0622, lng: 80.1692 },
  { city: 'Sriperumbudur', state: 'Tamil Nadu', displayName: 'Sriperumbudur, Tamil Nadu', lat: 12.9689, lng: 79.9426 },
  { city: 'Kanchipuram', state: 'Tamil Nadu', displayName: 'Kanchipuram, Tamil Nadu', lat: 12.8342, lng: 79.7035 },
  { city: 'Vellore', state: 'Tamil Nadu', displayName: 'Vellore, Tamil Nadu', lat: 12.9165, lng: 79.1325 },
  { city: 'Ranipet', state: 'Tamil Nadu', displayName: 'Ranipet, Tamil Nadu', lat: 12.9276, lng: 79.3327 },
  { city: 'Arcot', state: 'Tamil Nadu', displayName: 'Arcot, Tamil Nadu', lat: 12.9051, lng: 79.3197 },
  { city: 'Chittoor', state: 'Andhra Pradesh', displayName: 'Chittoor, Andhra Pradesh', lat: 13.2172, lng: 79.1003 },
  { city: 'Renigunta', state: 'Andhra Pradesh', displayName: 'Renigunta, Andhra Pradesh', lat: 13.6519, lng: 79.5125 },
  { city: 'Tiruchanur', state: 'Andhra Pradesh', displayName: 'Tiruchanur, Andhra Pradesh', lat: 13.6102, lng: 79.4347 },
];

export const searchLocations = async (query, options = {}) => {
  const trimmedQuery = (query || '').trim();
  if (trimmedQuery.length < 3) return [];

  const cacheKey = trimmedQuery.toLowerCase();
  const cached = getCachedSearchResults(cacheKey);
  if (cached) return cached;

  try {
    // Provider 1: OpenStreetMap Nominatim (best address quality). Limit to India.
    {
      const url =
        `${NOMINATIM_API}?q=${encodeURIComponent(trimmedQuery)}` +
        `&format=jsonv2&addressdetails=1&limit=8&countrycodes=in`;
      const data = await fetchJson(url, { signal: options.signal });
      const results = normalizeNominatimResults(data, trimmedQuery);
      cacheSearchResults(cacheKey, results);
      return results;
    }
  } catch (error) {
    if (error?.name === 'AbortError') return [];
    // Nominatim is frequently blocked/rate-limited in browsers; fall through to alternates.
  }

  // Provider 2: Photon (OSM-backed, typically CORS-friendly)
  try {
    // India bounding box: lon/lat approx (68,6) to (97,37)
    const url =
      `https://photon.komoot.io/api/?q=${encodeURIComponent(trimmedQuery)}` +
      `&limit=8&lang=en&bbox=68,6,97,37`;
    const data = await fetchJson(url, { signal: options.signal });
    const results = normalizePhotonResults(data, trimmedQuery);
    cacheSearchResults(cacheKey, results);
    return results;
  } catch (error) {
    if (error?.name === 'AbortError') return [];
  }

  // Provider 3: Open-Meteo geocoding (city/locality level, very reliable)
  try {
    const url =
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(trimmedQuery)}` +
      `&count=8&language=en&format=json&country=IN`;
    const data = await fetchJson(url, { signal: options.signal });
    const results = normalizeOpenMeteoResults(data, trimmedQuery);
    cacheSearchResults(cacheKey, results);
    return results;
  } catch (error) {
    if (error?.name === 'AbortError') return [];
  }

  // Last fallback: local database (limited, but offline-safe)
  const searchTerm = trimmedQuery.toLowerCase();
  const fallbackResults = LOCATION_DATABASE
    .filter(
      (loc) =>
        loc.city.toLowerCase().includes(searchTerm) ||
        loc.state.toLowerCase().includes(searchTerm) ||
        loc.displayName.toLowerCase().includes(searchTerm)
    )
    .map((loc) => ({
      ...loc,
      address: loc.displayName,
      description: loc.displayName,
      structured_formatting: {
        main_text: loc.city,
        secondary_text: loc.state
      }
    }));

  // Don’t “poison” cache for long when everything is down.
  cacheSearchResults(cacheKey, fallbackResults, fallbackResults.length ? SEARCH_CACHE_TTL_MS : ERROR_CACHE_TTL_MS);
  return fallbackResults;
};

// Get user's current location
export const getUserCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        // In a real app, you'd reverse geocode here
        // For demo, return a mock location
        resolve({
          displayName: 'Current Location',
          shortName: 'Current Location',
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      }
    );
  });
};

// Get distance between two locations with accurate calculation
export const getDistanceBetweenLocations = async (from, to) => {
  const cleanFrom = (from || '').trim();
  const cleanTo = (to || '').trim();
  if (!cleanFrom || !cleanTo) return null;

  // Try local DB first (fast), otherwise geocode via providers.
  const fromLocation = findLocationInDatabase(cleanFrom);
  const toLocation = findLocationInDatabase(cleanTo);

  const fromCoords =
    fromLocation
      ? {
          lat: fromLocation.lat,
          lon: fromLocation.lng,
          displayName: fromLocation.displayName,
          city: fromLocation.city,
          state: fromLocation.state
        }
      : await geocodeLocation(cleanFrom);

  const toCoords =
    toLocation
      ? {
          lat: toLocation.lat,
          lon: toLocation.lng,
          displayName: toLocation.displayName,
          city: toLocation.city,
          state: toLocation.state
        }
      : await geocodeLocation(cleanTo);

  if (!fromCoords || !toCoords) return null;
  if (
    typeof fromCoords.lat !== 'number' ||
    typeof fromCoords.lon !== 'number' ||
    typeof toCoords.lat !== 'number' ||
    typeof toCoords.lon !== 'number'
  ) {
    return null;
  }

  // Road distance via OSRM (best for pricing).
  try {
    const osrmResult = await calculateDistanceOSRM(fromCoords, toCoords);
    if (osrmResult) {
      return {
        distance: osrmResult.distance,
        duration: osrmResult.duration.hours,
        fromDetails: fromCoords.displayName || cleanFrom,
        toDetails: toCoords.displayName || cleanTo,
        source: 'osrm'
      };
    }
  } catch (error) {
    // fall back below
  }

  // Fallback: straight-line km (still real), with estimated duration.
  const distanceKm = calculateDistance(fromCoords.lat, fromCoords.lon, toCoords.lat, toCoords.lon);
  const estimatedMinutes = Math.max(5, Math.round((distanceKm / 50) * 60)); // ~50 km/h average
  const hours = Math.floor(estimatedMinutes / 60);
  const minutes = estimatedMinutes % 60;

  return {
    distance: Math.round(distanceKm),
    duration: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
    fromDetails: fromCoords.displayName || cleanFrom,
    toDetails: toCoords.displayName || cleanTo,
    source: 'straight-line'
  };
};

// Helper function to find location in database with fuzzy matching
const findLocationInDatabase = (searchString) => {
  if (!searchString) return null;
  
  const searchLower = searchString.toLowerCase();
  
  // Try exact match first
  let exactMatch = LOCATION_DATABASE.find(loc => 
    loc.city.toLowerCase() === searchLower ||
    loc.displayName.toLowerCase() === searchLower
  );
  
  if (exactMatch) return exactMatch;
  
  // Try partial match
  let partialMatch = LOCATION_DATABASE.find(loc => 
    loc.city.toLowerCase().includes(searchLower) ||
    searchLower.includes(loc.city.toLowerCase())
  );
  
  return partialMatch || null;
};

// Calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function toRad(degrees) {
  return degrees * (Math.PI/180);
}

export const geocodeLocation = async (locationName) => {
  // Check cache first
  if (locationCache.has(locationName)) {
    return locationCache.get(locationName);
  }

  const trimmed = (locationName || '').trim();
  if (!trimmed) return null;

  // Provider 0: local DB (offline-safe)
  const localMatch = findLocationInDatabase(trimmed);
  if (localMatch) {
    const localResult = {
      lat: localMatch.lat,
      lon: localMatch.lng,
      displayName: localMatch.displayName,
      city: localMatch.city || '',
      state: localMatch.state || ''
    };
    locationCache.set(locationName, localResult);
    return localResult;
  }

  // Provider 1: Open-Meteo (reliable place geocoding)
  try {
    const url =
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(trimmed)}` +
      `&count=5&language=en&format=json&country=IN`;
    const data = await fetchJson(url);
    const results = Array.isArray(data?.results) ? data.results : [];
    if (results.length > 0) {
      // Prefer the most "important" place (population), else first.
      const best = results.reduce((prev, cur) => {
        const prevPop = typeof prev?.population === 'number' ? prev.population : -1;
        const curPop = typeof cur?.population === 'number' ? cur.population : -1;
        return curPop > prevPop ? cur : prev;
      }, results[0]);

      const name = best.name || trimmed;
      const state = best.admin1 || '';
      const country = best.country || '';
      const displayName = [name, state || null, country || null].filter(Boolean).join(', ');

      const result = {
        lat: typeof best.latitude === 'number' ? best.latitude : undefined,
        lon: typeof best.longitude === 'number' ? best.longitude : undefined,
        displayName,
        city: name,
        state
      };
      locationCache.set(locationName, result);
      return result;
    }
  } catch (error) {
    // fall through
  }

  // Provider 2: Photon (OSM-backed, CORS-friendly) - prefer cities/towns over district boundaries.
  try {
    const url =
      `https://photon.komoot.io/api/?q=${encodeURIComponent(trimmed)}` +
      `&limit=10&lang=en&bbox=68,6,97,37`;
    const data = await fetchJson(url);
    const features = Array.isArray(data?.features) ? data.features : [];
    if (features.length > 0) {
      const scoreFeature = (feature) => {
        const props = feature?.properties || {};
        const osmKey = props.osm_key;
        const osmValue = props.osm_value;
        const type = props.type;

        let score = 0;
        if (osmKey === 'place') score += 100;
        if (osmKey === 'boundary') score -= 100;

        if (osmValue === 'city') score += 80;
        else if (osmValue === 'town') score += 70;
        else if (osmValue === 'village') score += 60;
        else if (osmValue === 'suburb') score += 40;
        else if (osmValue === 'hamlet') score += 30;
        else if (osmValue === 'administrative') score -= 60;

        if (type === 'city') score += 30;
        if (type === 'county') score -= 30;

        return score;
      };

      const best = features.reduce((prev, cur) => (scoreFeature(cur) > scoreFeature(prev) ? cur : prev), features[0]);

      const props = best.properties || {};
      const coords = best?.geometry?.coordinates;
      const lon = Array.isArray(coords) ? coords[0] : undefined;
      const lat = Array.isArray(coords) ? coords[1] : undefined;

      const name = props.name || props.street || props.city || props.state || trimmed;
      const city = props.city || props.county || '';
      const state = props.state || '';
      const country = props.country || '';
      const displayName = [name, city || null, state || null, country || null].filter(Boolean).join(', ');

      const result = {
        lat: typeof lat === 'number' ? lat : undefined,
        lon: typeof lon === 'number' ? lon : undefined,
        displayName,
        city,
        state
      };
      locationCache.set(locationName, result);
      return result;
    }
  } catch (error) {
    // fall through
  }

  // Provider 3: Nominatim (may be blocked in browsers)
  try {
    const url =
      `${NOMINATIM_API}?q=${encodeURIComponent(trimmed)}` +
      `&format=jsonv2&limit=1&addressdetails=1&countrycodes=in`;
    const data = await fetchJson(url);
    if (Array.isArray(data) && data.length > 0) {
      const first = data[0];
      const address = first.address || {};
      const result = {
        lat: first.lat ? parseFloat(first.lat) : undefined,
        lon: first.lon ? parseFloat(first.lon) : undefined,
        displayName: first.display_name || trimmed,
        city: address.city || address.town || address.village || address.county || '',
        state: address.state || ''
      };
      locationCache.set(locationName, result);
      return result;
    }
  } catch (error) {
    // fall through
  }

  return null;
};

export const calculateDistanceOSRM = async (fromCoords, toCoords) => {
  const cacheKey = `${fromCoords.lat},${fromCoords.lon}-${toCoords.lat},${toCoords.lon}`;
  
  // Check cache first
  if (distanceCache.has(cacheKey)) {
    return distanceCache.get(cacheKey);
  }

  try {
    const response = await fetch(
      `${OSRM_API}/${fromCoords.lon},${fromCoords.lat};${toCoords.lon},${toCoords.lat}?overview=false&geometries=geojson`
    );
    
    if (!response.ok) throw new Error('Distance calculation failed');
    
    const data = await response.json();
    
    if (data.routes && data.routes.length > 0) {
      // Distance is in meters, convert to kilometers
      const distanceInMeters = data.routes[0].distance;
      const distanceInKm = Math.round(distanceInMeters / 1000);
      const durationInSeconds = data.routes[0].duration;
      const durationInMinutes = Math.round(durationInSeconds / 60);
      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;
      
      const result = {
        distance: distanceInKm,
        duration: {
          minutes: durationInMinutes,
          hours: `${hours}h ${minutes}m`
        },
        route: data.routes[0]
      };
      
      // Cache the result
      distanceCache.set(cacheKey, result);
      return result;
    }
    
    return null;
  } catch (error) {
    console.error('OSRM error:', error);
    return null;
  }
};

// Fallback to straight-line distance (Haversine formula) if OSRM fails
export const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
};
